// @ts-check
import { button } from "./button.js";
import { h, uid } from "./dom.js";

/**
 * Confirmation dialog (DS-10), replacing window.confirm(). The confirm button
 * names the action ("Archive project"), and Escape or Cancel resolves false.
 *
 * `content` adds a preview under the body (CONV-03's restore and revert
 * previews); the dialog stays a confirmation, never a form.
 *
 * @param {{ title: string, body?: string, content?: Node, confirmText: string, cancelText?: string, destructive?: boolean }} options
 * @returns {Promise<boolean>}
 */
export function confirmAction({
  title,
  body,
  content,
  confirmText,
  cancelText = "Cancel",
  destructive = false,
}) {
  const titleId = uid("dialog-title");
  const bodyId = uid("dialog-body");
  const cancel = button({ text: cancelText, variant: "secondary" });
  const accept = button({
    text: confirmText,
    variant: destructive ? "destructive" : "primary",
  });
  const dialog = h(
    "dialog",
    {
      class: "ui-dialog",
      aria: {
        labelledby: titleId,
        describedby: body || content ? bodyId : undefined,
      },
    },
    h("h2", { class: "ui-dialog__title", id: titleId, text: title }),
    body && h("p", { class: "ui-dialog__body", id: bodyId, text: body }),
    content &&
      h(
        "div",
        { class: "ui-dialog__content", id: body ? undefined : bodyId },
        content,
      ),
    h("div", { class: "ui-dialog__actions" }, cancel, accept),
  );
  const returnFocus = /** @type {HTMLElement | null} */ (
    document.activeElement
  );
  document.body.append(dialog);
  return new Promise((resolve) => {
    /** @param {boolean} result */
    const finish = (result) => {
      dialog.close();
      dialog.remove();
      if (returnFocus?.isConnected) returnFocus.focus();
      resolve(result);
    };
    cancel.addEventListener("click", () => finish(false));
    accept.addEventListener("click", () => finish(true));
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      finish(false);
    });
    dialog.showModal();
    (destructive ? cancel : accept).focus();
  });
}

const destructiveVerbs = new Set([
  "Cancel",
  "Delete",
  "Discard",
  "Disconnect",
  "Forget",
  "Remove",
  "Revoke",
]);
/**
 * Confirm a question written as one message ("Delete notes.md?"). The question
 * becomes the title, anything after it the body, and the leading verb labels the
 * confirm button ("Delete"); destructive verbs get a destructive button.
 *
 * @param {string} message
 */
export function askToConfirm(message) {
  // Without <dialog> (unit tests, very old browsers) fall back to the native prompt.
  if (typeof HTMLDialogElement === "undefined") {
    const native = globalThis.confirm || globalThis.window?.confirm;
    return Promise.resolve(Boolean(native?.(message)));
  }
  const end = message.indexOf("?");
  const title = end === -1 ? message : message.slice(0, end + 1);
  const body = end === -1 ? undefined : message.slice(end + 1).trim();
  const verb = /^[A-Z][a-z]+/.exec(title)?.[0] || "Continue";
  return confirmAction({
    title,
    body: body || undefined,
    confirmText: verb === "Cancel" ? "Cancel it" : verb,
    cancelText: verb === "Cancel" ? "Keep it" : "Cancel",
    destructive: destructiveVerbs.has(verb),
  });
}
